/**
 * Referral Code Validation Utilities
 *
 * Matches backend validation rules from:
 * junkfun-backend/src/common-modules/invite/invite.util.ts
 */

// Allowed characters (excluding L, O, 1, 0 to avoid confusion)
const INVITE_CODE_CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const INVITE_CODE_LENGTH = 5;

/**
 * Validate if a referral/invite code is in the correct format
 * @param code - The code to validate
 * @returns true if valid, false otherwise
 */
export function isValidInviteCode(code: string): boolean {
  if (!code || typeof code !== 'string') {
    return false;
  }

  const pattern = new RegExp(`^[${INVITE_CODE_CHARS}]{${INVITE_CODE_LENGTH}}$`);
  return pattern.test(code.toUpperCase());
}

/**
 * Sanitize user input to only include valid characters
 * @param input - Raw user input
 * @returns Sanitized string with only valid characters
 */
export function sanitizeInviteCodeInput(input: string): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  return input
    .toUpperCase()
    .split('')
    .filter(char => INVITE_CODE_CHARS.includes(char))
    .join('')
    .slice(0, INVITE_CODE_LENGTH);
}

/**
 * Get validation error message for invalid code
 * @param code - The code to validate
 * @returns Error message or null if valid
 */
export function getInviteCodeError(code: string): string | null {
  if (!code || code.trim() === '') {
    return 'Please enter an invite code';
  }

  const trimmedCode = code.trim().toUpperCase();

  if (trimmedCode.length !== INVITE_CODE_LENGTH) {
    return `Invite code must be exactly ${INVITE_CODE_LENGTH} characters`;
  }

  const invalidChars = trimmedCode
    .split('')
    .filter(char => !INVITE_CODE_CHARS.includes(char));

  if (invalidChars.length > 0) {
    const invalidCharsStr = [...new Set(invalidChars)].join(', ');
    return `Invalid characters: ${invalidCharsStr}. Code can only contain: ${INVITE_CODE_CHARS}`;
  }

  return null;
}

/**
 * Format error message from backend API response
 * @param error - Error from API
 * @returns User-friendly error message
 */
export function formatBindingError(error: string): string {
  const errorMap: Record<string, string> = {
    'INVALID_INVITE_CODE': 'This invite code does not exist',
    'CANNOT_INVITE_YOURSELF': 'You cannot use your own invite code',
    'USER_ALREADY_HAS_INVITE_CODE': 'You have already bound an invite code',
    'CANNOT_USE_INVITEE_INVITE_CODE': 'Cannot create circular referral relationship',
    'INVALID_REQUEST': 'Invalid request. Please try again',
    'AUTHENTICATION_REQUIRED': 'Authentication required. Please refresh the page and sign in.',
    'NETWORK_ERROR': 'Network error. Please check your connection and try again.',
  };

  return errorMap[error] || error || 'An error occurred while binding the invite code';
}

/**
 * Get allowed characters info for display
 */
export function getAllowedCharsInfo(): string {
  return `Allowed characters: ${INVITE_CODE_CHARS}`;
}

/**
 * Get invite code requirements for display
 */
export function getInviteCodeRequirements(): string[] {
  return [
    `Must be exactly ${INVITE_CODE_LENGTH} characters`,
    'Only uppercase letters and numbers',
    'Excludes: L, O, 1, 0 (to avoid confusion)',
  ];
}
